import { CSSProperties, FC, ReactNode, Ref, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Breadcrumb, Button, Col, Drawer, Dropdown, Grid, Menu, Row, Tabs, Typography } from 'antd';
import {
    TbAdjustmentsHorizontal,
    TbArrowUpRight,
    TbCalendarShare,
    TbDotsVertical,
    TbFilePlus,
    TbInfoCircle,
    TbLogin2,
    TbPrinter,
} from 'react-icons/tb';

import { AppIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWorkspaceWhitelabelTheme } from '@/hooks/useWorkspaceWhitelabelTheme';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import PublicPageContextMenu from '@/shared/components/layouts/views/PublicPageContextMenu';
import { ThemeSegmented } from '@/shared/components/segmented';
import type { WhitelabelSharedResourcesSettings, WhitelabelWorkspaceThemeSettings } from '@/types';

export interface SharedResourceBreadcrumbItem {
    icon?: ReactNode;
    label: ReactNode;
}

export interface SharedResourceMetadataItemProps {
    avatarShape?: 'circle' | 'square';
    avatarSrc?: string;
    description?: ReactNode;
    icon: ReactNode;
    label: string;
    style?: CSSProperties;
    value?: ReactNode;
}

export type SharedResourceMetadataKey = 'author' | 'workspace' | 'company' | 'comments' | 'activity';

export type SharedResourceMetadataVisibility = Partial<Record<SharedResourceMetadataKey, boolean>>;

export const defaultSharedResourceMetadataVisibility: Record<SharedResourceMetadataKey, boolean> = {
    activity: false,
    author: true,
    comments: false,
    workspace: true,
    company: true,
};

export const getSharedResourceMetadataVisibility = (
    visibility?: SharedResourceMetadataVisibility,
): Record<SharedResourceMetadataKey, boolean> => ({
    ...defaultSharedResourceMetadataVisibility,
    ...(visibility ?? {}),
});

export const toExternalResourceHref = (url?: string) => {
    const trimmedUrl = url?.trim();

    if (!trimmedUrl) {
        return undefined;
    }

    return /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
};

interface SharedResourceMetadataGridItem extends SharedResourceMetadataItemProps {
    key: string;
    visible?: boolean;
}

export interface SharedResourceMetadataGridProps {
    items: SharedResourceMetadataGridItem[];
    style?: CSSProperties;
}

export interface SharedResourceShellProps {
    children: ReactNode;
    removeBranding?: boolean;
    sharedResources?: WhitelabelSharedResourcesSettings;
    workspaceTheme?: WhitelabelWorkspaceThemeSettings;
}

export interface SharedResourceDrawerItem {
    children: ReactNode | ((controls: { closeDrawer: () => void }) => ReactNode);
    icon?: ReactNode;
    key: string;
    label: ReactNode;
}

export interface SharedResourceContentProps {
    borderColor: string;
    children: ReactNode;
    contentRef?: Ref<HTMLElement>;
    loading?: boolean;
    maxWidth: number;
    style?: CSSProperties;
    textColor: string;
}

export interface SharedResourceHeaderProps {
    actionMenuExtraItems?: ReactNode;
    breadcrumbLabel: ReactNode;
    breadcrumbIcon?: ReactNode;
    mobileDrawerItems?: SharedResourceDrawerItem[];
    removeBranding?: boolean;
    sharedResources?: WhitelabelSharedResourcesSettings;
    title: ReactNode;
    workspaceName?: ReactNode;
}

export const withTokenAlpha = (color: string, alpha: number) => {
    const normalizedAlpha = Math.min(Math.max(alpha, 0), 1);
    const hex = color.match(/^#([0-9a-f]{6})$/i);

    if (hex) {
        const value = hex[1];
        const red = parseInt(value.slice(0, 2), 16);
        const green = parseInt(value.slice(2, 4), 16);
        const blue = parseInt(value.slice(4, 6), 16);

        return `rgba(${red}, ${green}, ${blue}, ${normalizedAlpha})`;
    }

    const rgb = color.match(/^rgba?\(([^)]+)\)$/i);

    if (rgb) {
        const [red, green, blue] = rgb[1].split(',').map((part) => part.trim());

        return `rgba(${red}, ${green}, ${blue}, ${normalizedAlpha})`;
    }

    return color;
};

export const SharedResourceMetadataItem: FC<SharedResourceMetadataItemProps> = ({
    avatarShape = 'square',
    avatarSrc,
    description,
    icon,
    label,
    style,
    value,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='top'
            wrap={false}
            gutter={[10, 0]}
            style={{
                height: '100%',
                minHeight: '4.25rem',
                padding: '0.75rem',
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                background: token.colorBgContainer,
                ...style,
            }}
        >
            <Col flex='none'>
                <Avatar
                    size={24}
                    shape={avatarShape}
                    src={avatarSrc}
                    icon={icon}
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        background: token.colorBgBase,
                        color: token.colorTextTertiary,
                    }}
                />
            </Col>
            <Col flex='auto' style={{ minWidth: 0 }}>
                <small style={{ display: 'block', color: token.colorTextTertiary }}>{label}</small>
                <Typography.Text
                    strong
                    style={{ display: 'block', color: token.colorText, lineHeight: '1.35rem' }}
                    ellipsis
                >
                    {value || 'Not provided'}
                </Typography.Text>
                {description && (
                    <Typography.Text
                        style={{ display: 'block', color: token.colorTextTertiary, fontSize: token.fontSizeSM }}
                        ellipsis
                    >
                        {description}
                    </Typography.Text>
                )}
            </Col>
        </Row>
    );
};

const hasMetadataValue = (value: ReactNode) =>
    value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '');

export const SharedResourceMetadataGrid: FC<SharedResourceMetadataGridProps> = ({ items, style }) => {
    const visibleItems = items.filter((item) => item.visible !== false && hasMetadataValue(item.value));
    const columnSpan = visibleItems.length === 1 ? 24 : visibleItems.length === 2 ? 12 : 8;

    if (!visibleItems.length) {
        return null;
    }

    return (
        <Row align='stretch' gutter={[12, 12]} style={{ margin: '1.5rem 0', ...style }}>
            {visibleItems.map((item) => {
                const { key, ...metadataItem } = item;

                return (
                    <Col key={key} xs={24} sm={12} lg={columnSpan} style={{ display: 'flex' }}>
                        <SharedResourceMetadataItem {...metadataItem} style={{ width: '100%' }} />
                    </Col>
                );
            })}
        </Row>
    );
};

export const SharedResourceContent: FC<SharedResourceContentProps> = ({
    borderColor,
    children,
    contentRef,
    loading = false,
    maxWidth,
    style,
    textColor,
}) => {
    const screens = Grid.useBreakpoint();
    const compact = !screens.lg;
    const mobile = !screens.sm;

    return (
        <main
            ref={contentRef}
            style={{
                boxSizing: 'border-box',
                color: textColor,
                flex: '1 0 auto',
                margin: '0 auto',
                maxWidth,
                minHeight: 0,
                padding: loading
                    ? compact
                        ? mobile
                            ? '1.25rem 1rem 5rem'
                            : '1.5rem 1.5rem 5.5rem'
                        : '2rem'
                    : compact
                      ? mobile
                          ? '1.25rem 1rem 5rem'
                          : '1.5rem 1.5rem 5.5rem'
                      : '2rem clamp(1rem, 4vw, 3rem) 6rem',
                borderLeft: compact ? undefined : `1px solid ${borderColor}`,
                borderRight: compact ? undefined : `1px solid ${borderColor}`,
                width: '100%',
                ...style,
            }}
        >
            {children}
        </main>
    );
};

export const SharedResourceHeader: FC<SharedResourceHeaderProps> = ({
    actionMenuExtraItems,
    breadcrumbIcon,
    breadcrumbLabel,
    mobileDrawerItems = [],
    removeBranding = false,
    sharedResources,
    title,
    workspaceName,
}) => {
    const { token } = useAntDesignTheme();
    const screens = Grid.useBreakpoint();
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const closeMobileDrawer = () => setMobileDrawerOpen(false);
    const headerSettings = sharedResources?.header;
    const headerBackground = withTokenAlpha(headerSettings?.backgroundColor || token.colorBgContainer, 0.78);
    const headerTextColor = headerSettings?.textColor || token.colorTextTertiary;
    const brandLabel = headerSettings?.brandName || workspaceName;
    const shouldShowWorkspaceName = headerSettings?.showWorkspaceName !== false;
    const shouldShowResourceType = headerSettings?.showResourceType !== false;
    const websiteHref = toExternalResourceHref(sharedResources?.footer?.website);
    // viewport-layout-allow: this is the outer boundary of a mobile bottom sheet.
    const height = mobileDrawerItems.length ? '76dvh' : 'auto';
    const breadcrumbItems = [
        shouldShowWorkspaceName && brandLabel
            ? {
                  title: (
                      <span style={{ color: headerTextColor }}>
                          {headerSettings?.logoUrl && (
                              <Avatar
                                  size={16}
                                  shape='square'
                                  src={headerSettings.logoUrl}
                                  style={{
                                      marginRight: '0.375rem',
                                      verticalAlign: '-0.175rem',
                                      border: `1px solid ${token.colorBorder}`,
                                  }}
                              />
                          )}
                          {brandLabel}
                      </span>
                  ),
              }
            : null,
        shouldShowResourceType
            ? {
                  title: (
                      <span
                          style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              color: headerTextColor,
                          }}
                      >
                          {breadcrumbIcon && (
                              <span
                                  style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flex: 'none',
                                      lineHeight: 0,
                                  }}
                              >
                                  {breadcrumbIcon}
                              </span>
                          )}
                          <span>{breadcrumbLabel}</span>
                      </span>
                  ),
              }
            : null,
        {
            title: (
                <Typography.Text style={{ maxWidth: '44rem' }} ellipsis>
                    {title}
                </Typography.Text>
            ),
        },
    ].filter(Boolean) as { title: ReactNode }[];

    const actionMenu = useMemo(
        () => (
            <DropdownMenuPanel style={{ marginTop: '1.5rem' }}>
                <Menu.Item
                    key='login'
                    icon={<TbLogin2 size='1rem' />}
                    onClick={() => {
                        window.open('https://dashboard.countercyclical.io/login', '_blank', 'noopener,noreferrer');
                    }}
                >
                    Log In
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                    key='print'
                    icon={<TbPrinter size='1rem' />}
                    onClick={() => {
                        window.print();
                    }}
                >
                    Print
                </Menu.Item>
                {actionMenuExtraItems}
                <Menu.Divider />
                <Menu.Item
                    key='whatIsCountercyclical'
                    icon={<img alt='Countercyclical' src={AppIcon} height={20} width={20} />}
                    onClick={() => {
                        window.open('https://countercyclical.io/about', '_blank', 'noopener,noreferrer');
                    }}
                >
                    What is Countercyclical?
                </Menu.Item>
                <Menu.Item
                    key='demo'
                    icon={<TbCalendarShare size='1rem' />}
                    onClick={() => {
                        window.open('https://cal.com/countercyclical/demo', '_blank', 'noopener,noreferrer');
                    }}
                >
                    Schedule a Demo
                </Menu.Item>
            </DropdownMenuPanel>
        ),
        [actionMenuExtraItems],
    );

    const mobileActionLabel = (icon: ReactNode, label: ReactNode) => (
        <span
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                gap: '0.75rem',
                lineHeight: '1.25rem',
                minWidth: 0,
            }}
        >
            <span
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    flex: '0 0 1.25rem',
                    height: '1.25rem',
                    justifyContent: 'center',
                    width: '1.25rem',
                }}
            >
                {icon}
            </span>
            <span>{label}</span>
        </span>
    );

    const mobileActionContent = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: mobileDrawerItems.length ? '100%' : undefined,
                minHeight: 0,
            }}
        >
            <Row align='middle' wrap={false} style={{ marginBottom: '0.75rem' }}>
                <Col flex='auto'>
                    <small style={{ color: token.colorTextTertiary }}>Appearance</small>
                </Col>
                <Col flex='none'>
                    <ThemeSegmented />
                </Col>
            </Row>
            <Menu selectable={false} style={{ borderInlineEnd: 0, flex: '0 0 auto', width: '100%' }}>
                <Menu.Item
                    key='login'
                    onClick={() => {
                        window.open('https://dashboard.countercyclical.io/login', '_blank', 'noopener,noreferrer');
                    }}
                >
                    {mobileActionLabel(<TbLogin2 size='1rem' />, 'Log In')}
                </Menu.Item>
                <Menu.Item
                    key='print'
                    onClick={() => {
                        window.print();
                    }}
                >
                    {mobileActionLabel(<TbPrinter size='1rem' />, 'Print')}
                </Menu.Item>
                {actionMenuExtraItems}
                <Menu.Divider />
                <Menu.Item
                    key='whatIsCountercyclical'
                    onClick={() => {
                        window.open('https://countercyclical.io/about', '_blank', 'noopener,noreferrer');
                    }}
                >
                    {mobileActionLabel(
                        <img alt='Countercyclical' src={AppIcon} height={18} width={18} style={{ display: 'block' }} />,
                        'What is Countercyclical?',
                    )}
                </Menu.Item>
                <Menu.Item
                    key='demo'
                    onClick={() => {
                        window.open('https://cal.com/countercyclical/demo', '_blank', 'noopener,noreferrer');
                    }}
                >
                    {mobileActionLabel(<TbCalendarShare size='1rem' />, 'Schedule a Demo')}
                </Menu.Item>
            </Menu>
            {!removeBranding && sharedResources?.footer?.showPoweredBy !== false && (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: '0 0 auto',
                        justifyContent: 'center',
                        marginTop: 'auto',
                        paddingTop: '1rem',
                    }}
                >
                    <Button
                        icon={<img alt='Countercyclical' src={AppIcon} height={18} width={18} />}
                        onClick={() => {
                            window.open(websiteHref || 'https://countercyclical.io', '_blank', 'noopener,noreferrer');
                        }}
                        style={{
                            alignItems: 'center',
                            backgroundColor: token.colorBgElevated,
                            display: 'inline-flex',
                        }}
                    >
                        {sharedResources?.footer?.website ? 'Visit Website' : 'Made with Countercyclical'}
                        <TbArrowUpRight style={{ color: token.colorTextTertiary }} />
                    </Button>
                </div>
            )}
        </div>
    );

    const mobileTabs = [
        ...mobileDrawerItems,
        {
            key: 'actions',
            label: 'Actions',
            icon: <TbAdjustmentsHorizontal size='1rem' />,
            children: mobileActionContent,
        },
    ].map(({ children, icon, label, ...item }) => ({
        ...item,
        children: typeof children === 'function' ? children({ closeDrawer: closeMobileDrawer }) : children,
        label: (
            <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.5rem', lineHeight: 1 }}>
                <span style={{ alignItems: 'center', display: 'inline-flex' }}>{icon}</span>
                <span>{label}</span>
            </span>
        ),
    }));
    const compact = !screens.lg;

    return (
        <>
            <Row
                align='middle'
                wrap={false}
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    alignSelf: 'start',
                    boxSizing: 'border-box',
                    minHeight: compact ? '3.25rem' : '3.5rem',
                    padding: compact ? '0 0.75rem' : '0 1rem',
                    background: headerBackground,
                    backdropFilter: 'blur(0.75rem) saturate(180%)',
                    WebkitBackdropFilter: 'blur(0.75rem) saturate(180%)',
                    borderBottom: `1px solid ${token.colorBorder}`,
                    width: '100%',
                }}
            >
                <Col flex='auto' style={{ minWidth: 0 }}>
                    {compact ? (
                        <Row align='middle' wrap={false} gutter={[8, 0]}>
                            {breadcrumbIcon && (
                                <Col
                                    flex='none'
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 0,
                                    }}
                                >
                                    {breadcrumbIcon}
                                </Col>
                            )}
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <Typography.Text strong ellipsis style={{ display: 'block', color: token.colorText }}>
                                    {title}
                                </Typography.Text>
                            </Col>
                        </Row>
                    ) : (
                        <Breadcrumb items={breadcrumbItems} />
                    )}
                </Col>
                <Col flex='none'>
                    {compact ? (
                        <Button
                            aria-label='Open shared resource information'
                            icon={<TbInfoCircle size='1rem' style={{ color: token.colorTextTertiary }} />}
                            onClick={() => setMobileDrawerOpen(true)}
                        />
                    ) : (
                        <Dropdown trigger={['click']} placement='bottomRight' popupRender={() => actionMenu}>
                            <Button aria-label='Open shared resource options' size='small'>
                                <TbDotsVertical
                                    size='0.75rem'
                                    style={{ verticalAlign: '-0.175rem', color: token.colorTextTertiary }}
                                />
                            </Button>
                        </Dropdown>
                    )}
                </Col>
            </Row>
            {compact && (
                <Drawer
                    destroyOnHidden
                    height={height}
                    onClose={closeMobileDrawer}
                    open={mobileDrawerOpen}
                    placement='bottom'
                    rootStyle={{ maxWidth: '100%', overflow: 'hidden' }}
                    title={
                        <Typography.Text ellipsis style={{ display: 'block', maxWidth: '100%' }}>
                            {title}
                        </Typography.Text>
                    }
                    styles={{
                        body: {
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto',
                            overflowX: 'hidden',
                            padding: '0 1rem calc(1rem + env(safe-area-inset-bottom))',
                            width: '100%',
                        },
                        content: {
                            boxSizing: 'border-box',
                            borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0`,
                            overflow: 'hidden',
                            width: '100%',
                        },
                    }}
                >
                    <style>
                        {`
                            .shared-resource-drawer-tabs > .ant-tabs-content-holder,
                            .shared-resource-drawer-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                            .shared-resource-drawer-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                                min-height: 0;
                                height: 100%;
                            }
                        `}
                    </style>
                    <Tabs
                        className='shared-resource-drawer-tabs'
                        items={mobileTabs}
                        size='small'
                        style={{ flex: '1 1 auto', maxWidth: '100%', minHeight: 0, width: '100%' }}
                    />
                </Drawer>
            )}
        </>
    );
};

export const SharedResourceFloatingActions: FC<{
    removeBranding?: boolean;
    sharedResources?: WhitelabelSharedResourcesSettings;
}> = ({ removeBranding = false, sharedResources }) => {
    const { token } = useAntDesignTheme();
    const screens = Grid.useBreakpoint();
    const websiteHref = toExternalResourceHref(sharedResources?.footer?.website);

    if (!screens.lg) {
        return null;
    }

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    left: '1rem',
                    bottom: '1rem',
                    zIndex: 20,
                }}
            >
                <ThemeSegmented />
            </div>
            {!removeBranding && sharedResources?.footer?.showPoweredBy !== false && (
                <Button
                    style={{
                        position: 'fixed',
                        right: '1rem',
                        bottom: '1rem',
                        zIndex: 20,
                        backgroundColor: token.colorBgElevated,
                        boxShadow: token.boxShadowSecondary,
                    }}
                    icon={
                        <img
                            src={AppIcon}
                            alt='Countercyclical'
                            height={18}
                            width={18}
                            style={{ verticalAlign: '-0.25rem' }}
                        />
                    }
                    onClick={() => {
                        window.open(websiteHref || 'https://countercyclical.io', '_blank', 'noopener,noreferrer');
                    }}
                >
                    {sharedResources?.footer?.website ? 'Visit Website' : 'Made with Countercyclical'}
                    <TbArrowUpRight style={{ color: token.colorTextTertiary }} />
                </Button>
            )}
        </>
    );
};

export const SharedResourceShell: FC<SharedResourceShellProps> = ({
    children,
    removeBranding,
    sharedResources,
    workspaceTheme,
}) => {
    const { themeName, token } = useAntDesignTheme();
    const { activateWorkspaceTheme, configureWorkspaceTheme, deactivateWorkspaceTheme } = useWorkspaceWhitelabelTheme();
    const themeNameRef = useRef(themeName);
    const contentSettings = sharedResources?.content;

    useEffect(() => {
        themeNameRef.current = themeName;
    }, [themeName]);

    useEffect(() => {
        if (!workspaceTheme) {
            return;
        }

        configureWorkspaceTheme(workspaceTheme);
        activateWorkspaceTheme();

        return () => {
            const fallbackTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

            deactivateWorkspaceTheme(fallbackTheme, themeNameRef.current);
        };
    }, [activateWorkspaceTheme, configureWorkspaceTheme, deactivateWorkspaceTheme, workspaceTheme]);

    return (
        <div
            style={{
                backgroundColor: contentSettings?.backgroundColor || token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                color: contentSettings?.textColor || token.colorTextBase,
                fontFamily: contentSettings?.fontFamily || undefined,
                overflowX: 'clip',
                overflowY: 'auto',
            }}
        >
            <PublicPageContextMenu includeDemo>{children}</PublicPageContextMenu>
            <SharedResourceFloatingActions removeBranding={removeBranding} sharedResources={sharedResources} />
        </div>
    );
};

export const sharedResourceTemplateMenuItem = (
    <Menu.Item icon={<TbFilePlus size='1rem' />} disabled key='duplicate'>
        Open in Workspace
    </Menu.Item>
);
